
import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  keywords?: string;
  structuredData?: Record<string, any>;
}

export function SEO({
  title = "Time Tracking Software for Any Workflow | Hour Tracker",
  description = "Time tracking software for any workflow that adapts to your business needs. Track billable hours, generate professional invoices, and get paid faster. Perfect for freelancers, consultants, and small businesses.",
  canonical = window.location.href,
  ogType = "website",
  ogImage = "/og-image.png",
  keywords = "time tracking software, any workflow, workflow optimization, invoice generator, freelancer tool, billable hours, productivity tracker, project management, flexible time tracking",
  structuredData,
}: SEOProps) {
  const siteTitle = title.includes("Hour Tracker") ? title : `${title} | Hour Tracker`;
  
  // Ensure the OG image path is absolute
  const absoluteOgImage = ogImage.startsWith('http') 
    ? ogImage 
    : `${window.location.origin}${ogImage}`;
  
  return (
    <Helmet>
      <title>{siteTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="google-site-verification" content="JBLksJ60yrYLEsivI23WSKg_zWfYZvckrZkPciwhNkQ" />
      <link rel="canonical" href={canonical} />
      <link rel="icon" href="/lovable-uploads/1cb2ed67-a994-4bc8-8c45-2e0bb7eba942.png" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Hour Tracker" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={siteTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      
      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Hour Tracker" />
      <meta name="revisit-after" content="7 days" />

      {/* Schema.org structured data for rich results */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}
